// Rich per-service page configuration. Each entry drives a premium service
// landing page (`<ServicePage />`). Content is derived from the client's
// existing service pages on cloudlabweb.com.

export const SERVICE_CONFIGS = {
  "software-web-development": {
    slug: "software-web-development",
    name: "Software Development",
    kicker: "Software · Web · Custom platforms",
    hero: {
      title: "Transforming ideas into",
      highlight: "production software.",
      subtitle:
        "We design, build, test and maintain software that runs the world's most ambitious businesses — from marketing sites to complex SaaS platforms and enterprise systems.",
      stats: [
        { v: "12+", l: "Years shipping software" },
        { v: "120+", l: "Projects delivered" },
        { v: "6", l: "Countries served" },
        { v: "24×5", l: "Delivery model" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Web, SaaS, enterprise & everything in between.",
      subtitle: "Cutting-edge stacks tuned to the specific needs of every client we serve.",
      items: [
        { icon: "Globe", title: "Web Applications", desc: "SaaS platforms, dashboards, portals and marketing sites — SEO-first, scalable and fast." },
        { icon: "Server", title: "Enterprise Software", desc: "ERPs, CRMs, HRMS and custom back-office platforms with role-based access." },
        { icon: "ShoppingBag", title: "E-Commerce", desc: "Custom storefronts, marketplaces and headless commerce built for conversion." },
        { icon: "Database", title: "APIs & Integrations", desc: "Robust REST/GraphQL APIs and middleware that connect every system in your stack." },
        { icon: "Cpu", title: "Real-time Systems", desc: "Chat, live-dashboards, IoT ingestion — engineered for reliability and scale." },
        { icon: "ShieldCheck", title: "Secure by Design", desc: "OWASP-hardened, encrypted, audit-logged software — enterprise-grade from day one." },
      ],
    },
    tech: {
      kicker: "Technologies we love",
      title: "The stack we ship every day.",
      items: [
        { name: "C#" }, { name: "PHP" }, { name: "Python" }, { name: "Java" },
        { name: "React" }, { name: "Next.js" }, { name: "Angular" }, { name: "Vue.js" },
        { name: "Node.js" }, { name: "TypeScript" }, { name: "MongoDB" }, { name: "PostgreSQL" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "A delivery playbook that respects your timelines.",
      items: [
        { title: "Discover", desc: "Blueprint every workflow, integration and success metric." },
        { title: "Design", desc: "Wireframes, prototypes and a UI system approved before we write code." },
        { title: "Build", desc: "Two-week sprints. Weekly demos. Continuous integration and testing." },
        { title: "Launch & scale", desc: "Deploy, monitor, iterate. We stick around long after go-live." },
      ],
    },
    outcomes: [
      "SEO-first architecture, scoring 95+ on Lighthouse",
      "Continuous integration + zero-downtime deployments",
      "Enterprise-grade security and access control",
      "Full ownership of code, infrastructure and IP",
    ],
    ctas: {
      primary: { label: "Start a project", to: "/quote" },
    },
  },

  "mobile-app-development": {
    slug: "mobile-app-development",
    name: "Mobile Application Development",
    kicker: "iOS · Android · Cross-platform",
    hero: {
      title: "Creating mobile experiences",
      highlight: "that matter.",
      subtitle:
        "Beautifully engineered mobile apps for iOS and Android — native performance, offline-first, App-Store-ready and built to drive real business outcomes.",
      stats: [
        { v: "iOS", l: "+ Android" },
        { v: "50+", l: "Apps published" },
        { v: "4.7★", l: "Avg. App Store rating" },
        { v: "1M+", l: "Users served" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every kind of mobile experience — done properly.",
      items: [
        { icon: "Apple", title: "iOS Development", desc: "Native Swift & SwiftUI apps built with the latest iOS SDK and Apple design guidelines." },
        { icon: "Smartphone", title: "Android Development", desc: "Kotlin-native apps built on the current Jetpack stack, ready for Play Store." },
        { icon: "Layers", title: "Cross-Platform", desc: "React Native and Flutter apps that share one codebase across iOS & Android." },
        { icon: "ShoppingBag", title: "M-Commerce", desc: "High-performance shopping apps with wallet, biometrics and offline sync." },
        { icon: "Users", title: "CRM & Field-Force", desc: "Rugged apps for sales, service and field-force teams — with offline data." },
        { icon: "Cloud", title: "SaaS Companion Apps", desc: "Consumer-grade mobile companions to your existing web SaaS." },
      ],
    },
    tech: {
      kicker: "Technologies we love",
      title: "Every stack you'd expect — and the ones you should.",
      items: [
        { name: "Swift" }, { name: "Kotlin" }, { name: "React Native" }, { name: "Flutter" },
        { name: "Ionic" }, { name: "Android Studio" }, { name: "Xcode" }, { name: "Firebase" },
        { name: "Expo" }, { name: "Realm" }, { name: "OneSignal" }, { name: "TestFlight" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From idea to App Store in weeks — not quarters.",
      items: [
        { title: "Scope", desc: "Feature map, monetisation model and delivery timeline." },
        { title: "Design", desc: "Native UI systems for both iOS + Android with clickable prototypes." },
        { title: "Build", desc: "Weekly TestFlight / Play Console builds — real feedback, early." },
        { title: "Launch", desc: "Store submission, ASO, and post-launch monitoring + iteration." },
      ],
    },
    outcomes: [
      "Native performance on iOS + Android",
      "Offline-first architecture with sync",
      "Push, biometrics, analytics & crash reporting",
      "App Store optimization and versioned releases",
    ],
    ctas: {
      primary: { label: "Start a project", to: "/quote" },
    },
  },

  "digital-marketing-seo": {
    slug: "digital-marketing-seo",
    name: "Social Media Marketing & SEO",
    kicker: "SMM · SEO · Content · Performance",
    hero: {
      title: "Creating a buzz",
      highlight: "around your brand.",
      subtitle:
        "Full-funnel social, SEO, content and paid strategy that compounds. We build audiences, drive traffic and convert it — engineered end-to-end for measurable growth.",
      stats: [
        { v: "6", l: "Channels · one plan" },
        { v: "3.2×", l: "Avg. traffic lift" },
        { v: "50+", l: "Brands scaled" },
        { v: "GA4", l: "Attribution ready" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "Full-suite services",
      title: "Elevate your brand with our complete stack.",
      items: [
        { icon: "MessageCircle", title: "Social Media Management", desc: "Content calendar, community, engagement and analytics across Facebook, Instagram, LinkedIn, Twitter and TikTok." },
        { icon: "Compass", title: "Strategy Planning", desc: "Data-driven strategies aligned to specific business goals — brand, traffic or leads." },
        { icon: "PenTool", title: "Content Creation", desc: "Copy, photography, motion graphics and video crafted for each platform." },
        { icon: "Search", title: "SEO & Research", desc: "Technical + on-page SEO, keyword research and competitor analysis." },
        { icon: "Megaphone", title: "Paid Campaigns", desc: "Facebook / Google / LinkedIn ads with creative testing and full-funnel tracking." },
        { icon: "BarChart3", title: "Reporting & Insights", desc: "GA4 + platform dashboards mapped to real revenue attribution." },
      ],
    },
    tech: {
      kicker: "Tools we use",
      title: "Best-in-class marketing tech.",
      items: [
        { name: "Ahrefs" }, { name: "SEMrush" }, { name: "Google Analytics 4" }, { name: "GSC" },
        { name: "Meta Ads" }, { name: "Google Ads" }, { name: "LinkedIn Ads" }, { name: "HubSpot" },
        { name: "Hootsuite" }, { name: "Canva" }, { name: "Adobe CC" }, { name: "Hotjar" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "Growth cycles that compound month over month.",
      items: [
        { title: "Audit", desc: "Deep-dive analysis of brand, channels, competitors and analytics." },
        { title: "Strategise", desc: "Playbook, calendar, target KPIs and quarterly milestones." },
        { title: "Execute", desc: "Content shipped weekly, campaigns launched, community managed." },
        { title: "Optimise", desc: "Monthly reports, A/B testing and iteration on what actually works." },
      ],
    },
    outcomes: [
      "Sustained organic traffic growth (3–10× baseline)",
      "Lead-generation funnels aligned to sales",
      "Brand consistency across every channel",
      "Full-funnel reporting tied to revenue",
    ],
    ctas: {
      primary: { label: "Start a campaign", to: "/quote" },
    },
  },

  "staff-augmentation": {
    slug: "staff-augmentation",
    name: "Staff Augmentation",
    kicker: "Vetted engineers · Embedded pods · Contract-to-hire",
    hero: {
      title: "The smart choice for",
      highlight: "business growth.",
      subtitle:
        "Access specialist engineers, designers and AI talent — embedded with your team in days. Contract, hybrid or fully-managed pods with senior CloudLab standards baked in.",
      stats: [
        { v: "48h", l: "Time to first CV" },
        { v: "1×", l: "Single spec to full squad" },
        { v: "80%", l: "Long-term conversion" },
        { v: "SLA", l: "Backed delivery" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "How we augment",
      title: "Four models — one bar for quality.",
      items: [
        { icon: "Users", title: "Basic Staffing", desc: "Individual specialists placed where you need them — engineering, design, PM, QA." },
        { icon: "Sparkles", title: "Skill-Based Squads", desc: "Cross-functional pods matched to a specific skill or workstream." },
        { icon: "Briefcase", title: "Project Managers", desc: "Seasoned PMs owning delivery, budget and stakeholder communication end-to-end." },
        { icon: "Headphones", title: "Managed Support", desc: "24×7 technical support, customer-service and back-office teams." },
        { icon: "Bot", title: "AI & Data Talent", desc: "ML engineers, prompt specialists and data scientists on demand." },
        { icon: "ShieldCheck", title: "Contract to Hire", desc: "Try before you commit — convert to full-time when the fit is proven." },
      ],
    },
    tech: {
      kicker: "Talent pool",
      title: "Every skill you'd expect from a modern engineering org.",
      items: [
        { name: "Full-stack Engineers" }, { name: "AI / ML Engineers" }, { name: "Mobile Engineers" },
        { name: "DevOps / SREs" }, { name: "UI / UX Designers" }, { name: "Product Managers" },
        { name: "QA / Test Engineers" }, { name: "Data Analysts" }, { name: "Customer Support" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From spec to seated in your Slack in under a week.",
      items: [
        { title: "Spec", desc: "Role, stack, seniority, time-zone and duration in a single call." },
        { title: "Match", desc: "Two to three vetted candidates on your desk within 48 hours." },
        { title: "Onboard", desc: "Access, tooling and stand-ups configured for a smooth day-one." },
        { title: "Deliver", desc: "SLA-backed delivery with weekly reviews and full transparency." },
      ],
    },
    outcomes: [
      "Senior-only talent — pre-vetted and reference-checked",
      "Time-zone aligned squads (Europe, GCC, US)",
      "SLA-backed delivery and reporting",
      "No lock-in — scale up or down monthly",
    ],
    ctas: {
      primary: { label: "Hire a specialist", to: "/quote" },
    },
  },

  "cloud-devops": {
    slug: "cloud-devops",
    name: "Cloud Infrastructure & DevOps",
    kicker: "AWS · Azure · GCP · Kubernetes · IaC",
    hero: {
      title: "Cloud infrastructure that",
      highlight: "keeps you shipping.",
      subtitle:
        "Battle-tested cloud architecture on AWS, Azure and GCP — with infrastructure-as-code, hardened CI/CD pipelines, observability, cost control and 24×7 SRE support built in.",
      stats: [
        { v: "3", l: "Clouds · AWS · Azure · GCP" },
        { v: "99.9%", l: "Uptime SLA" },
        { v: "IaC", l: "Terraform + Ansible" },
        { v: "24×7", l: "Monitoring & SRE" },
      ],
    },
    hero_image: "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/g17tbcvm_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2010_50_13%20PM_compressed.webp",
    capabilities: {
      kicker: "What we run",
      title: "Everything from server provisioning to blockchain services.",
      items: [
        { icon: "Cloud", title: "Cloud Architecture", desc: "Multi-region, multi-AZ AWS/Azure/GCP architectures built for reliability and cost." },
        { icon: "Server", title: "Server Management", desc: "Provisioning, scaling, monitoring and security across your entire fleet." },
        { icon: "Boxes", title: "Kubernetes", desc: "Production-grade K8s clusters with autoscaling, service mesh and GitOps." },
        { icon: "GitBranch", title: "CI/CD Pipelines", desc: "Zero-downtime deployments, canary releases and automated rollbacks." },
        { icon: "Database", title: "Managed Databases", desc: "RDS, Aurora, Cosmos DB — replication, backups and point-in-time recovery." },
        { icon: "ShieldCheck", title: "Security & Compliance", desc: "Least-privilege IAM, encryption at rest & in transit, audit-ready logging." },
        { icon: "BarChart3", title: "Analytics Services", desc: "Redshift, QuickSight, Kinesis pipelines for real-time analytics at any scale." },
        { icon: "Boxes", title: "Backup & DR", desc: "Automated backups, disaster recovery drills and RPO/RTO SLAs." },
      ],
    },
    tech: {
      kicker: "Platforms we operate",
      title: "The cloud stack we work with every day.",
      items: [
        { name: "AWS" }, { name: "Azure" }, { name: "Google Cloud" }, { name: "Kubernetes" },
        { name: "Terraform" }, { name: "Ansible" }, { name: "Docker" }, { name: "Helm" },
        { name: "GitHub Actions" }, { name: "GitLab CI" }, { name: "Datadog" }, { name: "Prometheus" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From assessment to always-on operations.",
      items: [
        { title: "Assess", desc: "Audit your current infra, workloads, cost and security posture." },
        { title: "Architect", desc: "Blueprint the target architecture — regions, services, IaC, guardrails." },
        { title: "Migrate", desc: "Zero-downtime migration with automated rollbacks and monitoring." },
        { title: "Operate", desc: "24×7 SRE, patch management, cost reviews and quarterly DR drills." },
      ],
    },
    outcomes: [
      "Multi-cloud architecture across AWS, Azure, GCP",
      "Infrastructure-as-code (Terraform + Ansible)",
      "CI/CD with zero-downtime deployments",
      "24×7 monitoring, alerting & incident response",
    ],
    ctas: {
      primary: { label: "Talk to a cloud architect", to: "/quote" },
    },
  },

  "ai-data-science": {
    slug: "ai-data-science",
    name: "Artificial Intelligence & Data Science",
    kicker: "AI · LLMs · Data · Automation",
    hero: {
      title: "AI systems that automate",
      highlight: "what teams used to do.",
      subtitle:
        "From LLM copilots and voice agents to computer-vision and predictive models — production-ready AI engineered around your business, not the other way around.",
      stats: [
        { v: "GPT-5", l: "+ Claude + Gemini" },
        { v: "40M+", l: "AI interactions served" },
        { v: "99%+", l: "Document extraction accuracy" },
        { v: "24×7", l: "Autonomous operation" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every kind of AI system your business needs.",
      items: [
        { icon: "Sparkles", title: "LLM Copilots", desc: "GPT / Claude / Gemini copilots grounded in your docs, data and workflows." },
        { icon: "Bot", title: "Voice & Chat Agents", desc: "24/7 AI agents that answer, qualify and book across web, WhatsApp and phone." },
        { icon: "FileSearch", title: "Document AI", desc: "OCR + LLMs for invoices, KYC, contracts, medical records — 99%+ accuracy." },
        { icon: "Eye", title: "Computer Vision", desc: "Quality-control, safety, biometrics and inventory vision systems at scale." },
        { icon: "LineChart", title: "Predictive Models", desc: "Forecasting, scoring and recommendation models on your data." },
        { icon: "Database", title: "Data Platforms", desc: "Data pipelines, warehouses, feature stores — the foundation for good AI." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "OpenAI" }, { name: "Claude" }, { name: "Gemini" }, { name: "LangChain" },
        { name: "Python" }, { name: "PyTorch" }, { name: "Vector DBs" }, { name: "Whisper" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "AI that goes live — not stays in a demo.",
      items: [
        { title: "Frame", desc: "Identify a valuable, measurable AI use-case tied to a real business metric." },
        { title: "Prototype", desc: "A working POC on your data in two weeks — decide with evidence." },
        { title: "Productionise", desc: "Guardrails, monitoring, feedback loops and cost controls." },
        { title: "Improve", desc: "Continuous evaluation and model updates as your data grows." },
      ],
    },
    outcomes: [
      "Grounded, guardrailed LLM systems (no hallucinations)",
      "Monitoring, logging & cost visibility from day one",
      "Human-in-the-loop for high-risk decisions",
      "Ownership of prompts, models & pipelines",
    ],
    ctas: {
      primary: { label: "Start an AI project", to: "/quote" },
    },
  },

  "crm-development": {
    slug: "crm-development",
    name: "CRM Development",
    kicker: "Custom CRM · Sales · Admissions · Field-force",
    hero: {
      title: "Custom CRM systems that",
      highlight: "actually get used.",
      subtitle:
        "Off-the-shelf CRMs make your team fight the software. We build custom CRMs shaped around your pipeline, your channels and your people — with AI copilots baked in.",
      stats: [
        { v: "60+", l: "CRM deployments" },
        { v: "12wk", l: "Typical go-live" },
        { v: "AI", l: "Copilot ready" },
        { v: "24×5", l: "Support" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Sales, service, admissions, field-force — all of it.",
      subtitle: "Custom CRMs, admissions CRMs, sales enablement platforms and dispatch systems — engineered around your actual workflow.",
      items: [
        { icon: "Users", title: "Sales CRM", desc: "Pipeline, forecasting, quoting, lead scoring and territory management for B2B sales teams." },
        { icon: "GraduationCap", title: "Admissions CRM", desc: "Enquiry → application → enrolment funnels for universities, colleges and education consultancies." },
        { icon: "Headphones", title: "Service CRM", desc: "Ticketing, SLAs, knowledge base and customer 360 for support-heavy operations." },
        { icon: "Truck", title: "Field-Force CRM", desc: "Route planning, offline visits, geo-check-ins and daily reporting for outside sales reps." },
        { icon: "MessageCircle", title: "Omni-channel Capture", desc: "Web forms, WhatsApp, voice, email, ads — every lead lands in one inbox, deduped and scored." },
        { icon: "Bot", title: "AI Copilot", desc: "Auto-summaries, next-best-action, smart follow-ups and voice-to-CRM logging." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "Enterprise-grade stack, chosen per project.",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Node.js" }, { name: "TypeScript" },
        { name: "PostgreSQL" }, { name: "MongoDB" }, { name: "Redis" }, { name: "WhatsApp API" },
        { name: "Twilio" }, { name: "OpenAI" }, { name: "AWS" }, { name: "Kubernetes" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From process audit to live CRM in 12 weeks.",
      items: [
        { title: "Process audit", desc: "Map every stage, hand-off, KPI and integration before we design a single screen." },
        { title: "Design", desc: "Clickable prototype of every user journey, signed off before we build." },
        { title: "Build", desc: "Two-week sprints. Live sandbox from week 3. Real users testing from week 6." },
        { title: "Migrate & train", desc: "Data cleanup, migration, role training and go-live support with SLAs." },
      ],
    },
    outcomes: [
      "Adoption rates that beat Salesforce / HubSpot (75%+ daily active)",
      "Full ownership of code, data and workflow",
      "AI copilots wired into your pipeline",
      "Native mobile CRM for field reps",
    ],
    ctas: {
      primary: { label: "Scope your CRM", to: "/quote" },
    },
  },

  "web-app-development": {
    slug: "web-app-development",
    name: "Web Application Development",
    kicker: "SaaS · Dashboards · Portals · Marketplaces",
    hero: {
      title: "Web applications engineered",
      highlight: "for scale, speed and revenue.",
      subtitle:
        "Complex web apps — SaaS platforms, admin dashboards, customer portals, internal tools and marketplaces — built with modern stacks and shipped every two weeks.",
      stats: [
        { v: "120+", l: "Web apps shipped" },
        { v: "95+", l: "Avg. Lighthouse score" },
        { v: "10wk", l: "MVP timeline" },
        { v: "SSR", l: "SEO-first" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every kind of web app your business needs.",
      items: [
        { icon: "LayoutDashboard", title: "Admin Dashboards", desc: "Data-heavy operator consoles with real-time metrics, RBAC and audit logs." },
        { icon: "Users", title: "Customer Portals", desc: "Self-service portals with billing, tickets, docs and API keys — branded and secure." },
        { icon: "ShoppingBag", title: "Marketplaces", desc: "Two-sided marketplaces with listings, payments, escrow and moderation tooling." },
        { icon: "Briefcase", title: "Internal Tools", desc: "Ops dashboards, back-office admin panels and workflow apps for your team." },
        { icon: "Globe", title: "Public Web Apps", desc: "Consumer-grade SaaS, community platforms and content-heavy sites with SEO." },
        { icon: "Zap", title: "Real-time Apps", desc: "Chat, collaboration, live dashboards and IoT ingestion at scale." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "The web stack we ship every day.",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Vue.js" }, { name: "Angular" },
        { name: "Node.js" }, { name: "TypeScript" }, { name: "PostgreSQL" }, { name: "MongoDB" },
        { name: "Redis" }, { name: "Tailwind" }, { name: "GraphQL" }, { name: "REST" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From wireframe to production in ten weeks.",
      items: [
        { title: "Discover", desc: "Blueprint every user flow, permission and integration point." },
        { title: "Design", desc: "Component library + high-fidelity prototype signed off in week 2." },
        { title: "Build", desc: "Weekly deploys, weekly demos, continuous integration and testing." },
        { title: "Launch", desc: "Zero-downtime deploy, monitoring, and a 30-day post-launch stabilisation window." },
      ],
    },
    outcomes: [
      "SEO-first architecture, 95+ Lighthouse",
      "Enterprise-grade security & role-based access",
      "Full source & IP ownership",
      "Modular architecture — extend without rewrites",
    ],
    ctas: {
      primary: { label: "Start a web app", to: "/quote" },
    },
  },

  "ecommerce-development": {
    slug: "ecommerce-development",
    name: "E-Commerce Development",
    kicker: "Storefronts · Headless · Marketplaces · D2C",
    hero: {
      title: "E-commerce platforms built",
      highlight: "for conversion.",
      subtitle:
        "Custom storefronts, headless commerce and marketplaces built on modern stacks — with performance, SEO and revenue at the centre of every decision.",
      stats: [
        { v: "50+", l: "Stores launched" },
        { v: "2.4×", l: "Avg. conversion lift" },
        { v: "PWA", l: "Mobile-native" },
        { v: "SEO", l: "First" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1607083206968-13611e3d76db?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every kind of e-commerce experience.",
      items: [
        { icon: "ShoppingBag", title: "Custom Storefronts", desc: "Fully bespoke D2C storefronts on Shopify Plus, WooCommerce or ground-up Next.js." },
        { icon: "Layers", title: "Headless Commerce", desc: "Headless setups (Shopify, Commerce.js, Medusa) with custom front-ends for speed & SEO." },
        { icon: "Globe", title: "Multi-region Stores", desc: "Multi-currency, multi-language, multi-tax stores with regional catalogs and pricing." },
        { icon: "Boxes", title: "Marketplaces", desc: "Multi-vendor marketplaces with onboarding, payouts, moderation and dispute flows." },
        { icon: "Zap", title: "Performance", desc: "Sub-2s page loads, edge caching, image optimisation and PWA-grade UX." },
        { icon: "Search", title: "SEO & Content", desc: "Clean URLs, structured data, blog engine and category-level SEO that ranks." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "Modern commerce infrastructure.",
      items: [
        { name: "Shopify Plus" }, { name: "WooCommerce" }, { name: "Medusa" }, { name: "Next.js" },
        { name: "Stripe" }, { name: "Klarna" }, { name: "Algolia" }, { name: "Contentful" },
        { name: "React" }, { name: "Node.js" }, { name: "PostgreSQL" }, { name: "Cloudflare" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From catalogue to launch in eight weeks.",
      items: [
        { title: "Strategy", desc: "Catalogue, pricing, funnel, checkout and post-purchase journey." },
        { title: "Design", desc: "Conversion-focused UI, mobile-first, with A/B-ready components." },
        { title: "Build", desc: "Checkout, payments, tax, shipping, inventory and CMS wired end-to-end." },
        { title: "Launch & grow", desc: "SEO launch, CRO experiments and email + retargeting hooked into GA4." },
      ],
    },
    outcomes: [
      "Conversion-optimised checkout & funnels",
      "Sub-2s page loads on every device",
      "Multi-currency, multi-language, multi-tax",
      "GA4 + attribution wired from day one",
    ],
    ctas: {
      primary: { label: "Start a store", to: "/quote" },
    },
  },

  "saas-development": {
    slug: "saas-development",
    name: "SaaS Development",
    kicker: "MVPs · Platforms · Vertical SaaS · Multi-tenant",
    hero: {
      title: "SaaS platforms built to",
      highlight: "raise, sell and scale.",
      subtitle:
        "From investor-ready MVPs to multi-tenant enterprise platforms — we architect, build and operate SaaS products with billing, RBAC, integrations and analytics baked in.",
      stats: [
        { v: "12wk", l: "MVP to launch" },
        { v: "40+", l: "SaaS products shipped" },
        { v: "Multi", l: "-tenant ready" },
        { v: "Stripe", l: "+ billing" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Everything a modern SaaS needs from day one.",
      items: [
        { icon: "Rocket", title: "MVP Engineering", desc: "Investor-ready MVP in 8–12 weeks — auth, billing, dashboards and one killer flow, done properly." },
        { icon: "Users", title: "Multi-tenant", desc: "Row-level isolation, org / team / user hierarchies, SSO and enterprise-friendly onboarding." },
        { icon: "CreditCard", title: "Billing & Payments", desc: "Stripe / Paddle billing, subscriptions, usage-based pricing, invoicing and dunning." },
        { icon: "ShieldCheck", title: "Security & SSO", desc: "SOC2-ready security, SAML/OIDC SSO, audit logs and API keys per tenant." },
        { icon: "Puzzle", title: "Integrations", desc: "Native integrations, webhooks, Zapier apps, public API and OpenAPI docs." },
        { icon: "LineChart", title: "Analytics & Growth", desc: "Product analytics (PostHog / Mixpanel), attribution, cohorts and PLG loops." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "The modern SaaS stack.",
      items: [
        { name: "Next.js" }, { name: "React" }, { name: "Node.js" }, { name: "TypeScript" },
        { name: "PostgreSQL" }, { name: "Prisma" }, { name: "Redis" }, { name: "Stripe" },
        { name: "Clerk / Auth0" }, { name: "PostHog" }, { name: "Vercel" }, { name: "AWS" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From napkin to first paying customer.",
      items: [
        { title: "Frame", desc: "Sharpen the ICP, JTBD, pricing and the one flow that has to work perfectly." },
        { title: "Design", desc: "Clickable prototype of core loops + a design system you can extend forever." },
        { title: "Build", desc: "Auth → onboarding → core loop → billing → dashboards, shipped in six sprints." },
        { title: "Launch & iterate", desc: "First customers, first invoices, then a growth cadence of weekly experiments." },
      ],
    },
    outcomes: [
      "Investor-ready MVP in 8–12 weeks",
      "Multi-tenant, billing-ready foundation",
      "Product analytics + PLG loops from day one",
      "Enterprise-friendly SSO / RBAC / audit logs",
    ],
    ctas: {
      primary: { label: "Start your SaaS", to: "/quote" },
    },
  },

  "erp-development": {
    slug: "erp-development",
    name: "ERP Development",
    kicker: "Custom ERPs · HRMS · Finance · Manufacturing · Campus",
    hero: {
      title: "Custom ERP systems that",
      highlight: "run the whole business.",
      subtitle:
        "Bespoke ERPs for education, manufacturing, distribution and services — one platform for finance, HR, ops, inventory and analytics, engineered around how you actually work.",
      stats: [
        { v: "20+", l: "ERPs deployed" },
        { v: "Campus", l: "+ Enterprise" },
        { v: "Modules", l: "20+ per system" },
        { v: "24×7", l: "Support" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every module a modern ERP needs.",
      items: [
        { icon: "DollarSign", title: "Finance & Accounting", desc: "GL, AR, AP, budgets, taxation, multi-currency and audit-grade reporting." },
        { icon: "Users", title: "HRMS & Payroll", desc: "Onboarding, leaves, attendance, appraisals, payroll and statutory compliance." },
        { icon: "Boxes", title: "Inventory & Procurement", desc: "Multi-warehouse inventory, purchase orders, vendor management and reorder rules." },
        { icon: "Factory", title: "Manufacturing", desc: "BOM, work orders, MRP, quality control and shop-floor tracking." },
        { icon: "GraduationCap", title: "Campus ERP", desc: "Admissions, fees, academics, exams, hostel, transport and library — end-to-end." },
        { icon: "BarChart3", title: "Analytics", desc: "Live dashboards, KPI trees, role-based reports and export-to-Excel everywhere." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "Enterprise stack, cloud-native by default.",
      items: [
        { name: "PostgreSQL" }, { name: "MongoDB" }, { name: "Node.js" }, { name: "Python" },
        { name: ".NET" }, { name: "React" }, { name: "Angular" }, { name: "Kubernetes" },
        { name: "AWS" }, { name: "Azure" }, { name: "Power BI" }, { name: "Metabase" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From audit to full-org rollout.",
      items: [
        { title: "Audit", desc: "Map every department, workflow, integration and reporting need in a two-week sprint." },
        { title: "Design", desc: "Module blueprint, role matrix, data model and integration architecture signed off." },
        { title: "Build", desc: "Modules delivered every 4–6 weeks — go live incrementally, not big-bang." },
        { title: "Migrate & train", desc: "Data migration, role training, parallel-run, then cutover with SLA support." },
      ],
    },
    outcomes: [
      "One platform replacing 5–10 point tools",
      "Live dashboards for every business unit",
      "Full ownership of code, data & customisations",
      "24×7 support with defined SLAs",
    ],
    ctas: {
      primary: { label: "Scope your ERP", to: "/quote" },
    },
  },

  // ============================================================
  //  Phase 3.1 — Commercial service landing pages (canonical set)
  //  These 7 entries + existing crm/mobile/saas make the 10-page
  //  canonical service architecture. Old slugs (ai-data-science,
  //  software-web-development, web-app-development) still render
  //  via aliasing in ServiceDetail.jsx but declare the new URL as
  //  their canonical so Google consolidates over time.
  // ============================================================

  "ai-development": {
    slug: "ai-development",
    name: "AI Development",
    kicker: "AI · LLMs · Automation · Copilots · Voice agents",
    hero: {
      title: "AI Development Company for",
      highlight: "real-world business automation.",
      subtitle:
        "CloudLab designs and develops production AI systems that plug into your existing business — CRMs, ERPs, portals, WhatsApp, phone lines and internal tools — with LLM copilots, voice agents, document intelligence and decision-support baked in, not bolted on.",
      stats: [
        { v: "GPT-5", l: "+ Claude + Gemini" },
        { v: "40M+", l: "AI interactions served" },
        { v: "Grounded", l: "on your data" },
        { v: "24×7", l: "Autonomous operation" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "AI systems that actually go to production.",
      items: [
        { icon: "Sparkles", title: "LLM Copilots", desc: "GPT / Claude / Gemini copilots grounded in your docs, CRM, databases and internal tools — with guardrails, cost controls and audit logs." },
        { icon: "Bot", title: "AI Agents", desc: "Autonomous agents that qualify leads, book meetings, handle tickets or run internal workflows end-to-end." },
        { icon: "Phone", title: "AI Voice Agents", desc: "24/7 voice agents on real phone numbers — SDR, support, receptionist, admissions counsellor." },
        { icon: "FileSearch", title: "Document Intelligence", desc: "OCR + LLMs for invoices, KYC, medical, admissions, legal or compliance documents — 99%+ accuracy on structured extraction." },
        { icon: "MessageCircle", title: "Conversational AI", desc: "Web, WhatsApp, email or in-app AI — one agent trained on your operation, deployed across every channel." },
        { icon: "LineChart", title: "Decision Support", desc: "Forecasting, scoring, recommendation and predictive models on your live business data." },
      ],
    },
    tech: {
      kicker: "Stack",
      title: "Modern AI stack, production-hardened.",
      items: [
        { name: "OpenAI" }, { name: "Anthropic Claude" }, { name: "Gemini" }, { name: "LangChain" },
        { name: "Python" }, { name: "PyTorch" }, { name: "Pinecone" }, { name: "Weaviate" },
        { name: "Whisper" }, { name: "Twilio" }, { name: "Retell" }, { name: "AWS Bedrock" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "AI that ships — not stays in a demo.",
      items: [
        { title: "Frame", desc: "Identify a valuable, measurable AI use-case tied to a real business metric — not an experiment." },
        { title: "Prototype", desc: "A working POC on your data in two weeks — decide with evidence, not a slide deck." },
        { title: "Productionise", desc: "Guardrails, monitoring, cost controls, feedback loops, PII handling — the boring bits that make AI trustworthy." },
        { title: "Improve", desc: "Continuous evaluation and model updates as your data and product evolve." },
      ],
    },
    outcomes: [
      "AI systems grounded on your live business data",
      "Guardrailed, monitored, cost-controlled deployments",
      "Native integration with CRM, ERP and internal tools",
      "Full ownership of prompts, models and pipelines",
    ],
    ctas: { primary: { label: "Start an AI project", to: "/quote" } },
  },

  "ai-agent-development": {
    slug: "ai-agent-development",
    name: "AI Agent Development",
    kicker: "Autonomous agents · Multi-step reasoning · Tool use · Voice",
    hero: {
      title: "AI Agent Development for",
      highlight: "the operations that actually run your business.",
      subtitle:
        "We build task-completing AI agents — not chatbots. Agents that qualify leads on WhatsApp, book meetings in your calendar, raise tickets in your CRM, and run multi-step operations across your existing tools with a supervised, auditable loop.",
      stats: [
        { v: "Tool-use", l: "Native" },
        { v: "Multi-step", l: "Reasoning" },
        { v: "Guardrailed", l: "By default" },
        { v: "Auditable", l: "Every action logged" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Agents that finish tasks, not just talk.",
      items: [
        { icon: "PhoneCall", title: "SDR & Sales Agents", desc: "Inbound + outbound agents that qualify, book meetings, hand-off to humans and log everything in your CRM." },
        { icon: "Headphones", title: "Support Agents", desc: "L1 support agents that answer, deflect, escalate and update tickets — trained on your product docs and history." },
        { icon: "GraduationCap", title: "Admissions Agents", desc: "Voice / chat agents for education — enquiries, application status, document collection, interview prep." },
        { icon: "Briefcase", title: "Internal Ops Agents", desc: "Employee-facing agents that pull data across tools, generate reports and complete internal workflows." },
        { icon: "Workflow", title: "Multi-tool Orchestration", desc: "Agents that reason across CRM, calendar, WhatsApp, email, database and payments in a single supervised loop." },
        { icon: "ShieldCheck", title: "Guardrails & Auditing", desc: "Every action logged, every dangerous tool gated, every LLM output evaluated. No unsupervised autonomy." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "GPT / Claude / Gemini" }, { name: "LangGraph" }, { name: "LangChain" },
        { name: "OpenAI Assistants" }, { name: "Retell Voice" }, { name: "Twilio" },
        { name: "WhatsApp Business" }, { name: "n8n / Zapier" }, { name: "Vector DBs" },
        { name: "Postgres" }, { name: "Redis" }, { name: "AWS Bedrock" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From use case to a shipped, monitored agent.",
      items: [
        { title: "Use-case audit", desc: "Which task is worth automating? What data, tools and guardrails does the agent need?" },
        { title: "Design", desc: "Tool schemas, escalation rules, evaluation criteria and human-in-the-loop review points — before we write a prompt." },
        { title: "Build & Evaluate", desc: "Iterative build with automated evaluation on real business scenarios, not toy prompts." },
        { title: "Deploy & Monitor", desc: "Production deploy with logging, cost controls and continuous evaluation as data changes." },
      ],
    },
    outcomes: [
      "Agents that complete tasks — not just chat",
      "Full audit trail of every agent action",
      "Human-in-the-loop review where risk warrants it",
      "Cost-controlled, guardrailed by default",
    ],
    ctas: { primary: { label: "Design an AI agent", to: "/quote" } },
  },

  "custom-software-development": {
    slug: "custom-software-development",
    name: "Custom Software Development",
    kicker: "Bespoke systems · Enterprise · Integrations · Modernisation",
    hero: {
      title: "Custom Software Development that",
      highlight: "runs the business — not slideware.",
      subtitle:
        "CloudLab builds bespoke software for businesses that have outgrown spreadsheets, off-the-shelf SaaS or legacy systems. Custom platforms, enterprise back-office, integrations and internal tooling — designed around how your team actually works.",
      stats: [
        { v: "120+", l: "Systems shipped" },
        { v: "12+ yrs", l: "Delivering software" },
        { v: "Own", l: "Your code & data" },
        { v: "24×5", l: "Delivery model" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Bespoke systems for the operations that matter.",
      items: [
        { icon: "Layers", title: "Custom business platforms", desc: "Bespoke systems for sales, operations, admissions, service or field teams — one platform, your process." },
        { icon: "Server", title: "Enterprise back-office", desc: "Finance, HR, procurement, inventory, approvals and reporting — replacing 5–10 point tools with one operating system." },
        { icon: "Puzzle", title: "Integrations & middleware", desc: "APIs and middleware connecting Salesforce, Xero, HubSpot, WhatsApp, Twilio, HMRC, banking and legacy databases." },
        { icon: "LayoutDashboard", title: "Internal tooling", desc: "Ops dashboards, back-office admin, moderation and analytics apps for internal teams." },
        { icon: "Wrench", title: "Legacy modernisation", desc: "Extracting business logic from legacy PHP / .NET / desktop systems into modern cloud-native platforms." },
        { icon: "Cpu", title: "AI-enabled software", desc: "LLM copilots, document intelligence and predictive models embedded into custom software from day one." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Node.js" }, { name: "TypeScript" },
        { name: "Python" }, { name: ".NET" }, { name: "PostgreSQL" }, { name: "MongoDB" },
        { name: "Redis" }, { name: "AWS" }, { name: "Azure" }, { name: "Kubernetes" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "Structured delivery — not open-ended staffing.",
      items: [
        { title: "Discovery", desc: "Paid 1–3 week discovery mapping every workflow, integration, KPI and edge case." },
        { title: "Design", desc: "Clickable prototype signed off before build — measure twice, cut once." },
        { title: "Build", desc: "Two-week sprints, weekly demos, working software from week three." },
        { title: "Launch & support", desc: "Zero-downtime deploy, plus an SLA-backed support plan for post-launch." },
      ],
    },
    outcomes: [
      "One platform replacing 5–10 point tools",
      "Own the code, IP and infrastructure",
      "Structured delivery on written fixed scope",
      "Modular architecture — extend without rewrites",
    ],
    ctas: { primary: { label: "Start a custom project", to: "/quote" } },
  },

  "web-application-development": {
    slug: "web-application-development",
    name: "Web Application Development",
    kicker: "Dashboards · Portals · SaaS · Marketplaces · Internal Tools",
    hero: {
      title: "Web Application Development for",
      highlight: "serious business software.",
      subtitle:
        "CloudLab builds complex web applications — SaaS platforms, admin dashboards, customer portals, marketplaces and internal tools — engineered for scale, security and speed. SEO-first architecture, 95+ Lighthouse, real-time where you need it.",
      stats: [
        { v: "120+", l: "Web apps shipped" },
        { v: "95+", l: "Avg. Lighthouse" },
        { v: "10wk", l: "MVP timeline" },
        { v: "SSR", l: "SEO-first" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Every kind of web application your business needs.",
      items: [
        { icon: "LayoutDashboard", title: "Admin Dashboards", desc: "Data-heavy operator consoles with real-time metrics, RBAC and audit logs." },
        { icon: "Users", title: "Customer Portals", desc: "Self-service portals with billing, tickets, docs and API keys — branded and secure." },
        { icon: "ShoppingBag", title: "Marketplaces", desc: "Two-sided marketplaces with listings, payments, escrow and moderation tooling." },
        { icon: "Briefcase", title: "Internal Tools", desc: "Ops dashboards, back-office admin panels and workflow apps for your team." },
        { icon: "Globe", title: "Public Web Apps", desc: "Consumer-grade SaaS, community platforms and content-heavy sites with SEO." },
        { icon: "Zap", title: "Real-time Apps", desc: "Chat, collaboration, live dashboards and IoT ingestion at scale." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Vue.js" }, { name: "TypeScript" },
        { name: "Node.js" }, { name: "PostgreSQL" }, { name: "MongoDB" }, { name: "Redis" },
        { name: "Tailwind" }, { name: "GraphQL" }, { name: "AWS" }, { name: "Vercel" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "From wireframe to production in ten weeks.",
      items: [
        { title: "Discover", desc: "Blueprint every user flow, permission and integration point." },
        { title: "Design", desc: "Component library + high-fidelity prototype signed off in week 2." },
        { title: "Build", desc: "Weekly deploys, weekly demos, CI + automated testing." },
        { title: "Launch", desc: "Zero-downtime deploy, monitoring, 30-day post-launch stabilisation." },
      ],
    },
    outcomes: [
      "SEO-first architecture, 95+ Lighthouse",
      "Enterprise-grade security & RBAC",
      "Own the code, IP and infrastructure",
      "Modular — extend without rewrites",
    ],
    ctas: { primary: { label: "Start a web app", to: "/quote" } },
  },

  "edtech-development": {
    slug: "edtech-development",
    name: "EdTech Software Development",
    kicker: "Admissions · LMS · Student portals · Campus ERPs · AI for education",
    hero: {
      title: "EdTech Software Development that",
      highlight: "runs enrolment, teaching and operations.",
      subtitle:
        "CloudLab builds EdTech products for universities, colleges, education agencies and EdTech founders — admissions CRMs, LMS platforms, student portals, campus ERPs and AI-assisted admissions. Software that runs the institution, not just a single feature.",
      stats: [
        { v: "12+ yrs", l: "Delivering EdTech" },
        { v: "Campus", l: "+ EdTech founders" },
        { v: "Multi-campus", l: "Ready" },
        { v: "AI-native", l: "Admissions" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Software across the entire student journey.",
      items: [
        { icon: "MessageCircle", title: "Admissions CRMs", desc: "Enquiry → application → enrolment funnels with WhatsApp, phone and web capture, lead scoring and automation." },
        { icon: "Users", title: "Student Portals", desc: "Self-service portals for applicants and students — documents, fees, transcripts, communication." },
        { icon: "GraduationCap", title: "LMS & Online Teaching", desc: "Custom LMS, live-class integrations, assessments and progress tracking — Canvas / Moodle where fitting." },
        { icon: "Building2", title: "Campus ERPs", desc: "Finance, HR, academics, hostel, transport, library and reporting — one platform for the whole institution." },
        { icon: "Sparkles", title: "AI Admissions", desc: "AI voice agents for pre-admission interviews, counselling, document processing and application triage." },
        { icon: "Smartphone", title: "Education Mobile Apps", desc: "Student, parent and staff apps with offline access, notifications and secure single sign-on." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Node.js" }, { name: "PostgreSQL" },
        { name: "MongoDB" }, { name: "React Native" }, { name: "OpenAI" }, { name: "Twilio" },
        { name: "WhatsApp API" }, { name: "AWS" }, { name: "Stripe" }, { name: "Xero" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "Delivered incrementally, live in weeks.",
      items: [
        { title: "Discover", desc: "Map every admission, academic and operational workflow before any code." },
        { title: "Design", desc: "Clickable prototype for admissions, portal and back-office signed off by stakeholders." },
        { title: "Build & Migrate", desc: "Two-week sprints, live sandbox from week 3, data migration in parallel." },
        { title: "Go-live & Support", desc: "Staged roll-out with training, SLA support and continuous improvement." },
      ],
    },
    outcomes: [
      "Full stack of admissions → LMS → ERP capability",
      "AI-native admissions (voice + document intelligence)",
      "Multi-campus / multi-country by default",
      "Own the code, IP and student data",
    ],
    ctas: { primary: { label: "Scope your EdTech project", to: "/quote" } },
  },

  "mvp-development": {
    slug: "mvp-development",
    name: "MVP Development",
    kicker: "Idea → prototype → MVP → paying customer",
    hero: {
      title: "MVP Development that reaches",
      highlight: "the first paying customer.",
      subtitle:
        "CloudLab helps founders and product teams go from napkin to production MVP in 8–12 weeks — with auth, one killer workflow, billing, dashboards and enough polish to onboard real users, raise a round or defend a pilot.",
      stats: [
        { v: "8–12wk", l: "MVP to launch" },
        { v: "Investor", l: "-ready builds" },
        { v: "Stripe", l: "+ billing baked in" },
        { v: "Scale", l: "-ready foundation" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "The right MVP — not a demo.",
      items: [
        { icon: "Rocket", title: "SaaS MVPs", desc: "Multi-tenant SaaS with auth, one core workflow, dashboards and Stripe billing — investor-ready in 8–12 weeks." },
        { icon: "Smartphone", title: "Mobile MVPs", desc: "React Native / Flutter apps shipped to TestFlight and Play Store early, iterated on real users." },
        { icon: "MessageCircle", title: "AI-native MVPs", desc: "MVPs where AI is the product — copilots, agents, AI-assisted workflows — with the eval + guardrails to survive users." },
        { icon: "Layers", title: "Two-sided marketplaces", desc: "Focused marketplace MVPs with listings, payments and moderation — enough to prove the loop." },
        { icon: "Puzzle", title: "Prototype validation", desc: "Two-week clickable prototypes to validate the flow with real users before writing production code." },
        { icon: "ShieldCheck", title: "Scale-ready foundation", desc: "Architecture chosen so v2 is an extension, not a rewrite." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "Next.js" }, { name: "React Native" }, { name: "TypeScript" },
        { name: "PostgreSQL" }, { name: "Prisma" }, { name: "Stripe" },
        { name: "Clerk / Auth0" }, { name: "PostHog" }, { name: "Vercel" },
        { name: "OpenAI" }, { name: "Tailwind" }, { name: "shadcn/ui" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "Idea → validation → build → launch → iterate.",
      items: [
        { title: "Idea validation", desc: "Sharpen the ICP, JTBD, pricing and the one flow that has to work perfectly." },
        { title: "Prototype", desc: "Two-week clickable prototype tested with real target users — decide with evidence." },
        { title: "MVP build", desc: "6–10 week production build: auth → onboarding → core loop → billing → dashboards." },
        { title: "Launch & iterate", desc: "First paying customers, first invoices, then a weekly experimentation cadence." },
      ],
    },
    outcomes: [
      "Investor / pilot-ready MVP in 8–12 weeks",
      "Real users on real production infrastructure",
      "Scale-ready foundation — no rewrites for v2",
      "Own the code, IP and analytics from day one",
    ],
    ctas: { primary: { label: "Scope your MVP", to: "/quote" } },
  },

  "software-modernisation": {
    slug: "software-modernisation",
    name: "Software Modernisation",
    kicker: "Legacy replatform · Cloud migration · Refactor · AI-enablement",
    hero: {
      title: "Software Modernisation without",
      highlight: "a big-bang rewrite.",
      subtitle:
        "CloudLab modernises legacy systems — PHP monoliths, .NET desktop apps, VB6, ageing Java, on-prem ERPs — in structured increments. Extract the business logic, move to modern cloud-native stacks, add AI, keep the lights on the entire time.",
      stats: [
        { v: "Legacy", l: "→ Modern cloud" },
        { v: "Zero", l: "-downtime cutover" },
        { v: "Refactor", l: "+ Extend + AI" },
        { v: "Incremental", l: "Never big-bang" },
      ],
    },
    hero_image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build",
      title: "Extract, modernise, extend — safely.",
      items: [
        { icon: "Wrench", title: "Legacy code audit", desc: "Structured audit of legacy PHP / .NET / VB / Java / desktop systems — risk map, cost estimate, phased plan." },
        { icon: "Cloud", title: "Cloud migration", desc: "On-prem → AWS / Azure / GCP with modern CI/CD, monitoring and IaC. UK, EU or region-specific hosting where required." },
        { icon: "Layers", title: "Replatform & refactor", desc: "Move the business logic to a modern stack (React / Next.js / Node / .NET / Python) without breaking day-to-day operations." },
        { icon: "Cpu", title: "AI enablement", desc: "Add LLM copilots, document intelligence or predictive models to legacy systems — often the fastest ROI before a full rewrite." },
        { icon: "Puzzle", title: "API-first extension", desc: "Wrap legacy systems in modern APIs so new products, integrations and mobile apps can be built on top." },
        { icon: "ShieldCheck", title: "Security & compliance", desc: "GDPR, ISO 27001-aligned architecture, encryption at rest, audit logging and role-based access." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: ".NET" }, { name: "Node.js" },
        { name: "Python" }, { name: "PostgreSQL" }, { name: "MongoDB" }, { name: "Redis" },
        { name: "AWS" }, { name: "Azure" }, { name: "Docker" }, { name: "Kubernetes" },
      ],
    },
    steps: {
      kicker: "How we work",
      title: "Never big-bang — always incremental.",
      items: [
        { title: "Audit", desc: "Full legacy audit — risks, dependencies, real user flows, phased modernisation plan." },
        { title: "Stabilise", desc: "Stop the bleeding: monitoring, CI, tests, security patches — before we change anything else." },
        { title: "Modernise", desc: "Replatform incrementally — one module at a time, with parallel-run and rollback plans." },
        { title: "Extend", desc: "Once modernised, add the AI, integrations and new products the legacy system blocked." },
      ],
    },
    outcomes: [
      "Legacy business logic preserved, modernised, extended",
      "Zero-downtime, incremental migration",
      "AI copilots on top of previously locked-down systems",
      "Own the modernised code and infrastructure",
    ],
    ctas: { primary: { label: "Audit our legacy system", to: "/quote" } },
  },
};
